/* eslint-disable react-hooks/rules-of-hooks */
import { ApiError, PaginatedResponseWithCountDto } from "@/client/extend";
import { PermissionsDto } from "@/client";
import {
  PositionFeature,
  PositionState,
} from "@/components/custom/data-table/data-table-position";
import {
  PreviewFeature,
  PreviewState,
} from "@/components/custom/data-table/data-table-preview";
import { OptionType } from "@/components/select-input/select-input";
import { CLEAR_FLAG, withoutClearFlag } from "@/lib/clear-flag";
import { asStateProperty, functionalUpdateMap, isDefined } from "@/lib/utils";
import {
  QueryKey,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  getFacetedRowModel,
  getFacetedUniqueValues,
  getSortedRowModel,
  OnChangeFn,
  RowPinningState,
} from "@tanstack/react-table";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";
import { functionalUpdate } from "@tanstack/react-table";
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnSizingState,
  PaginationState,
  Row,
  SortingState,
  Table,
  VisibilityState,
} from "@tanstack/react-table";
import React, { MutableRefObject, useContext, useRef, useEffect } from "react";
import { FieldPath, PathValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { createNanoEvents, Emitter } from "nanoevents";
import { GalleryFeature, GalleryState } from "./data-table-gallery";
import { RowDef } from "./data-table-box";
import {
  AllSelectedFeature,
  AllSelectedState,
} from "./data-table-all-selected";
import {
  AccordionGroupingState,
  AccordionGroupingActions,
  useAccordionGrouping,
} from "./data-table-accordion";
import {
  RowClassNameFeature,
  RowClassNameOptions,
} from "./data-table-row-class";
import { useCurrentRef } from "@/hooks/use-current-ref";
import {
  TableSettingsState,
  useTableSettingsState,
} from "./table-settings/use-table-settings";
import { parseSortValues } from "./table-settings/table-settings-layout";

export type PaginationRequest = { page: number; limit: number };

export type Useable<T> = T | (() => T);
export const use = <T,>(useable: Useable<T>) =>
  typeof useable === "function" ? (useable as () => T)() : useable;

const isDefault = (v: any, def: any) =>
  Array.isArray(def) ? def.includes(v) : v === def;

type SortDirection = "asc" | "desc";

type DefaultSortValue =
  | string
  | number
  | {
      field: string | number;
      dir?: SortDirection;
    };

const normalizeSortValues = (
  value: unknown,
  defaultDirection: SortDirection = "asc",
): string[] => {
  const values = Array.isArray(value) ? value : isDefined(value) ? [value] : [];

  return values
    .map((item) => {
      if (!isDefined(item)) {
        return undefined;
      }

      if (typeof item === "string") {
        const trimmed = item.trim();
        if (!trimmed) {
          return undefined;
        }

        // Keep JSON payloads as-is, e.g. from URL/state hydration.
        if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
          return trimmed;
        }

        return JSON.stringify({ field: trimmed, dir: defaultDirection });
      }

      if (typeof item === "object") {
        if ("field" in (item as any)) {
          const field = (item as any).field;
          if (!isDefined(field)) {
            return undefined;
          }
          return JSON.stringify({
            field,
            dir: (item as any).dir ?? defaultDirection,
          });
        }

        return JSON.stringify(item);
      }

      return JSON.stringify({ field: item, dir: defaultDirection });
    })
    .filter((item): item is string => isDefined(item));
};

export type WithSetter<T> = [T, React.Dispatch<React.SetStateAction<T>>];

export const TableContext = React.createContext<Table<unknown>>(
  undefined as unknown as Table<unknown>,
);
export const useTable = () => useContext(TableContext);

export const TableQueryContext = React.createContext<
  UseQueryOptions<PaginatedResponseWithCountDto<unknown>, ApiError>
>(
  undefined as unknown as UseQueryOptions<
    PaginatedResponseWithCountDto<unknown>,
    ApiError
  >,
);
export const useTableQuery = () => useContext(TableQueryContext);

export const TableQueryResultContext = React.createContext<
  UseQueryResult<PaginatedResponseWithCountDto<unknown>, ApiError>
>(
  undefined as unknown as UseQueryResult<
    PaginatedResponseWithCountDto<unknown>,
    ApiError
  >,
);
export const useTableQueryResult = () => useContext(TableQueryResultContext);
export const TableTotalQueryContext = React.createContext<
  UseQueryOptions<number, ApiError>
>(undefined as unknown as UseQueryOptions<number, ApiError>);
export const useTableTotalQuery = () => useContext(TableTotalQueryContext);
export const TableTotalQueryResultContext = React.createContext<
  UseQueryResult<number, ApiError>
>(undefined as unknown as UseQueryResult<number, ApiError>);
export const useTableTotalQueryResult = () =>
  useContext(TableTotalQueryResultContext);

export type TableTotal = {
  value?: number;
  isEstimate?: boolean;
  temporaryEstimate?: number;
  isLoading?: boolean;
};
export const TableTotalContext = React.createContext<TableTotal>(
  undefined as unknown as TableTotal,
);
export const useTableTotal = () => React.useContext(TableTotalContext);

export const TableCountQueryContext = React.createContext<
  UseQueryOptions<number, ApiError>
>(undefined as unknown as UseQueryOptions<number, ApiError>);
export const useTableCountQuery = () => useContext(TableCountQueryContext);
export const TableCountQueryResultContext = React.createContext<
  UseQueryResult<number, ApiError>
>(undefined as unknown as UseQueryResult<number, ApiError>);
export const useTableCountQueryResult = () =>
  useContext(TableCountQueryResultContext);

export type TableCount = {
  value?: number;
  isEstimate?: boolean;
  temporaryEstimate?: number;
  isLoading?: boolean;
};
export const TableCountContext = React.createContext<TableCount>(
  undefined as unknown as TableCount,
);
export const useTableCount = () => React.useContext(TableCountContext);

export const TableQueryArgumentContext = React.createContext<
  WithSetter<PaginationRequest>
>(undefined as unknown as WithSetter<PaginationRequest>);
export const useTableQueryArgument = () =>
  useContext(TableQueryArgumentContext);

export const TableQueryOptionsContext = React.createContext<
  WithSetter<Partial<UseQueryOptions<any>>>
>(undefined as unknown as WithSetter<Partial<UseQueryOptions<any>>>);
export const useTableQueryOptions = () => useContext(TableQueryOptionsContext);

export const TableColumnsContext = React.createContext<ColumnDef<unknown>[]>(
  undefined as unknown as ColumnDef<unknown>[],
);
export const useTableColumns = () => useContext(TableColumnsContext);

export const TableBoxContext = React.createContext<RowDef<unknown>>(
  undefined as unknown as RowDef<unknown>,
);
export const useTableBox = () => useContext(TableBoxContext);

export const TableRowSelectionContext = React.createContext<
  WithSetter<RowSelectionState>
>(undefined as unknown as WithSetter<RowSelectionState>);
export const useTableRowSelection = () => useContext(TableRowSelectionContext);

export const TableColumnVisibilityContext = React.createContext<
  WithSetter<VisibilityState>
>(undefined as unknown as WithSetter<VisibilityState>);
export const useTableColumnVisibility = () =>
  useContext(TableColumnVisibilityContext);

export const TableColumnFilterContext = React.createContext<
  WithSetter<ColumnFiltersState>
>(undefined as unknown as WithSetter<ColumnFiltersState>);
export const useTableColumnFilter = () => useContext(TableColumnFilterContext);

export const TableSortingContext = React.createContext<
  WithSetter<SortingState>
>(undefined as unknown as WithSetter<SortingState>);
export const useTableSorting = () => useContext(TableSortingContext);

export const TableColumnSizingContext = React.createContext<
  WithSetter<ColumnSizingState>
>(undefined as unknown as WithSetter<ColumnSizingState>);
export const useTableColumnSizing = () => useContext(TableColumnSizingContext);

export const TablePositionContext = React.createContext<
  WithSetter<PositionState>
>(undefined as unknown as WithSetter<PositionState>);
export const useTablePosition = () => useContext(TablePositionContext);

export const TablePreviewContext = React.createContext<
  WithSetter<PreviewState>
>(undefined as unknown as WithSetter<PreviewState>);
export const useTablePreview = () => useContext(TablePreviewContext);

export const TableGalleryContext = React.createContext<
  WithSetter<GalleryState>
>(undefined as unknown as WithSetter<GalleryState>);
export const useTableGallery = () => useContext(TableGalleryContext);

export const TableAllSelectedContext = React.createContext<
  WithSetter<AllSelectedState>
>(undefined as unknown as WithSetter<AllSelectedState>);
export const useTableAllSelected = () => useContext(TableAllSelectedContext);

export const TableAccordionGroupingContext = React.createContext<{
  state: AccordionGroupingState;
  actions: AccordionGroupingActions;
}>(
  undefined as unknown as {
    state: AccordionGroupingState;
    actions: AccordionGroupingActions;
  },
);
export const useTableAccordionGrouping = () =>
  useContext(TableAccordionGroupingContext);

export const TableRowClassNameOptionsContext = React.createContext<
  RowClassNameOptions<unknown>
>(undefined as unknown as RowClassNameOptions<unknown>);
export const useTableRowClassNameOptions = () =>
  useContext(TableRowClassNameOptionsContext);

export const TablePaginationContext = React.createContext<
  WithSetter<PaginationState>
>(undefined as unknown as WithSetter<PaginationState>);
export const useTablePagination = () => useContext(TablePaginationContext);

export const TableEventsContext = React.createContext<Emitter>(
  undefined as unknown as Emitter,
);
export const useTableEvents = () => useContext(TableEventsContext);

export interface AutoRefreshConfig<TRow = any> {
  lookFor: (rows: TRow[]) => boolean; // function to determine if a refresh is needed based on current rows
  interval?: number; // in milliseconds, default 5000 (5 seconds)
  maxRefreshes?: number; // optional limit on number of refreshes
}

export const useTableRefreshCounts = () => {
  const queryClient = useQueryClient();
  const tableTotalQuery = useContext(TableTotalQueryContext);
  // const tableCountQuery = useContext(TableCountQueryContext);
  return () => {
    if (tableTotalQuery.queryKey) {
      queryClient.invalidateQueries({ queryKey: tableTotalQuery.queryKey });
    }
    // if (tableCountQuery.queryKey) {
    //   queryClient.invalidateQueries({ queryKey: tableCountQuery.queryKey });
    // }
  };
};

export const useTableRefresh = () => {
  const queryClient = useQueryClient();
  const tableQuery = useContext(TableQueryContext);
  const tableTotalQuery = useContext(TableTotalQueryContext);
  // const tableCountQuery = useContext(TableCountQueryContext);
  const eventsEmitter = useContext(TableEventsContext);
  return () => {
    queryClient.invalidateQueries({ queryKey: tableQuery.queryKey });
    if (tableTotalQuery.queryKey) {
      queryClient.invalidateQueries({ queryKey: tableTotalQuery.queryKey });
    }
    // if (tableCountQuery.queryKey) {
    //   queryClient.invalidateQueries({ queryKey: tableCountQuery.queryKey });
    // }
    eventsEmitter.emit("refresh");
  };
};

// Common "delete/hide row" action: refetches the table's list (+ total) query, then
// closes the side-by-side preview panel if it was showing the row and that row is no
// longer present in the refetched data (covers both hard deletes, where the row is
// always gone, and status toggles like "disable" that only hide the row under some
// filters — the panel stays open if the row is still visible under the current filter).
// Closing goes through `table.closePreview()` rather than `setPreview(0)`: the row is gone,
// so the child route addressing it has to go too, or the deleted id lingers in the URL. The
// numeric table position is kept, so the user stays where they were in the list.
export const useTableDeleteRefresh = () => {
  const queryClient = useQueryClient();
  const tableQuery = useContext(TableQueryContext);
  const tableTotalQuery = useContext(TableTotalQueryContext);
  // const tableCountQuery = useContext(TableCountQueryContext);
  const eventsEmitter = useContext(TableEventsContext);
  const table = useContext(TableContext);
  // `positionalId`: pass true for tables whose id is the row's array index
  // (e.g. queue pending/failures, deleted by position) rather than a stable
  // entity id. Deleting any row at or before the previewed index shifts a
  // different row into that index, so the previewed index no longer refers
  // to the same record even though "something" is still there — close
  // whenever the deleted index is <= the previewed one, not just on exact
  // match.
  return async (id: string | number, positionalId = false) => {
    await queryClient.invalidateQueries({ queryKey: tableQuery.queryKey });
    if (tableTotalQuery.queryKey) {
      await queryClient.invalidateQueries({
        queryKey: tableTotalQuery.queryKey,
      });
    }
    // if (tableCountQuery.queryKey) {
    //   queryClient.invalidateQueries({
    //     queryKey: tableCountQuery.queryKey,
    //   });
    // }
    eventsEmitter.emit("refresh");
    if (!table) return;
    const position = table.getState().position;
    if (position?.id === undefined) return;
    if (positionalId) {
      const deletedIndex = Number(id);
      const previewedIndex = Number(position.id);
      if (
        !Number.isNaN(deletedIndex) &&
        !Number.isNaN(previewedIndex) &&
        deletedIndex <= previewedIndex
      ) {
        table.closePreview();
      }
      return;
    }
    if (String(position.id) !== String(id)) {
      return;
    }
    // `table` is a stale closure captured at the last render before this
    // click, so its row model still reflects pre-delete data even after the
    // invalidateQueries above resolves. Check the freshly refetched cache
    // data directly instead of table.getRow, which would never throw here.
    const getRowId = table?.options?.getRowId;
    if (!getRowId) return;
    const freshData = queryClient.getQueryData<
      PaginatedResponseWithCountDto<unknown>
    >(tableQuery.queryKey);
    const stillPresent = getRowId
      ? freshData?.results.some(
          (row, index) => getRowId(row, index, undefined) === String(id),
        )
      : false;
    if (!stillPresent) {
      table.closePreview();
    }
  };
};

export type TableSearchContextValue<T = string, K = any> = {
  searchLabel?: string;
  searchField?: T;
  searchValidation?: (v: T) => boolean;
  searchState: WithSetter<K>;
};
export const TableSearchContext = React.createContext<TableSearchContextValue>(
  undefined as unknown as TableSearchContextValue,
);
export const useTableSearch = () => React.useContext(TableSearchContext);

export type TableFiltersContextValue<TArgument> = {
  defaultFilters?: Partial<{
    [k in keyof TArgument]: TArgument[k] | TArgument[k][];
  }>;
  defaultFiltersApply?: Partial<{
    [k in keyof TArgument]: TArgument[k];
  }>;
  filterBreadcrumbs?: {
    /** Translation keys of chip labels that mean something of their own here -- see filterChipLabelKey. */
    labelKeys?: Partial<Record<keyof TArgument, string>>;
    formatters?: Partial<
      Record<
        keyof TArgument,
        (value: TArgument[keyof TArgument]) => React.ReactNode
      >
    >;
    hiddenKeys?: (keyof TArgument)[];
  };
  isFiltersActive?: boolean;
  clearFilters: () => void;
};
export const TableFiltersContext = React.createContext<
  TableFiltersContextValue<PaginationRequest>
>(undefined as unknown as TableFiltersContextValue<PaginationRequest>);
export const useTableFilters = () => React.useContext(TableFiltersContext);

export type TableSortOptionContextValue<T = any> = {
  sortEnum?: T[];
  sortOptions?: OptionType<T>[];
  sortOptionState: WithSetter<T>;
  getSortOption: (value: T) => OptionType<T> | undefined;
};
export const TableSortOptionContext =
  React.createContext<TableSortOptionContextValue>(
    undefined as unknown as TableSortOptionContextValue,
  );
export const useTableSortOption = () =>
  React.useContext(TableSortOptionContext);

export type TableSettingsContextValue = TableSettingsState;
export const TableSettingsContext =
  React.createContext<TableSettingsContextValue>(
    undefined as unknown as TableSettingsContextValue,
  );
export const useTableSettings = () => useContext(TableSettingsContext);

export const TableItemTypeContext = React.createContext<string>(
  undefined as unknown as string,
);
export const useTableItemType = () => useContext(TableItemTypeContext);

export const TableCurrentItemRefContext = React.createContext<
  React.MutableRefObject<HTMLDivElement | null>
>(undefined as unknown as React.MutableRefObject<HTMLDivElement | null>);
export const useTableCurrentItemRef = () =>
  useContext(TableCurrentItemRefContext);

export const TableAutoRefreshContext = React.createContext<AutoRefreshConfig[]>(
  undefined as unknown as AutoRefreshConfig[],
);
export const useTableAutoRefresh = () => useContext(TableAutoRefreshContext);

export type TPinnedRowMeta = {
  __meta?: { pinned?: boolean };
};

export type TPinnedRow<TRow = any> = TRow & TPinnedRowMeta;

export type TablePinnedRowContextValue<TRow = any> = WithSetter<
  TPinnedRow<TRow>
>;
export const TablePinnedRowContext =
  React.createContext<TablePinnedRowContextValue>(
    undefined as unknown as TablePinnedRowContextValue,
  );
export const useTablePinnedRow = () => React.useContext(TablePinnedRowContext);

export type TableContextProps<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
> = {
  queryCreator: (
    query: TArgument,
  ) => UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>;
  queryArgumentState?: Useable<WithSetter<TArgument>>;
  queryArgument?: Useable<TArgument>;
  onQueryArgumentChange?: (q: TArgument) => void;
  queryOptionsState?: Useable<
    WithSetter<Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>>
  >;
  queryOptions?: Useable<
    Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>
  >;
  columns: Useable<ColumnDef<TRow>[]>;
  getRowId?: (originalRow: TRow, index: number, parent?: Row<TRow>) => string;
  manualPagination?: boolean;
  skipPreview?: Useable<boolean>;
  // Not `Useable` - `use()` would call it. See `PreviewOptions.onPreviewClose`.
  onPreviewClose?: () => void;
  previewState?: Useable<WithSetter<PreviewState>>;
  preview?: Useable<PreviewState>;
  galleryState?: Useable<WithSetter<GalleryState>>;
  gallery?: Useable<GalleryState>;
  enableGallery?: Useable<boolean>;
  allSelectedState?: Useable<WithSetter<AllSelectedState>>;
  allSelected?: Useable<AllSelectedState>;
  box?: RowDef<TRow>;
  positionState?: Useable<WithSetter<PositionState>>;
  position?: Useable<PositionState>;
  groupByColumn?: Useable<string>;

  searchField?: Useable<TSearchField>;
  searchLabel?: Useable<string>;
  searchValidation?: (v: any) => boolean;
  defaultFilters?: Useable<
    Partial<{ [k in keyof TArgument]: TArgument[k] | TArgument[k][] }>
  >;
  filterBreadcrumbs?: Useable<{
    /** Translation keys of chip labels that mean something of their own here -- see filterChipLabelKey. */
    labelKeys?: Partial<Record<keyof TArgument, string>>;
    formatters?: Partial<
      Record<
        keyof TArgument,
        (value: TArgument[keyof TArgument]) => React.ReactNode
      >
    >;
    hiddenKeys?: (keyof TArgument)[];
  }>;
  sortField?: Useable<TSortField>;
  defaultSort?: Useable<DefaultSortValue | DefaultSortValue[]>;
  getSortOption?: (value: any) => OptionType<any> | undefined;
  sortEnum: readonly PathValue<TArgument, TSortField>[];

  /**
   * Opts the table into the column manager and per-user persistence (ID-6897): the user's saved
   * layout for this key is applied from /me and changes are written back. Lower-case dotted ids,
   * e.g. "data.sessions"; one key per table, shared by every route that mounts the same context.
   */
  tableKey?: string;
  itemType?: string;
  totalQueryCreator?: (
    query: TArgument,
  ) => UseQueryOptions<number, TError, number, any>;
  countQueryCreator?: (
    query: TArgument,
  ) => UseQueryOptions<number, TError, number, any>;
  ignoreQueryCount?: boolean;
  /**
   * When enabled, if the count query returns faster than the items query
   * and count < limit, the items request is cancelled and re-issued with
   * count as the new limit. Special case: count === 0 just cancels and shows
   * "no results" without fetching items.
   */
  enableCountOptimization?: boolean;
  currentRef?: Useable<MutableRefObject<HTMLDivElement | null>>;
  autoRefresh?: AutoRefreshConfig[];
  enableMultiRowSelection?: boolean;
  pinnedRowState?: WithSetter<TPinnedRow<TRow>>;
  pinnedRow?: TPinnedRow<TRow>;
  /**
   * Enables a "Download CSV" button for this table. Declarative — set this to turn the button
   * on for a new table; the button itself lives in DownloadCsvButton (data-table/buttons).
   */
  downloadCsv?: {
    url: () => string;
    fileName: string;
    // Pure selector over the already-fetched permissions object — not a hook, so it can be
    // called from plain (non-component) code in DownloadCsvButton without violating hook rules.
    permission?: (permissions: PermissionsDto) => boolean;
  };
} & RowClassNameOptions<TRow>;

const both = <PropsA, PropsB>(A: React.FC<PropsA>, B: React.FC<PropsB>) => {
  const component: React.FC<PropsA & { children?: React.ReactNode }> = ({
    children,
    ...props
  }) => (
    <A {...(props as any)}>
      <B {...(props as any)}>{children}</B>
    </A>
  );
  component.displayName = A.displayName;
  return component;
};

export const createTableContext = <
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
>(
  _default?: Partial<
    TableContextProps<
      TRow,
      TArgument,
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TSearchField,
      TSortField
    >
  >,
) => {
  const M_TableContext = React.createContext<Table<TRow>>(
    undefined as unknown as Table<TRow>,
  );
  const TableProvider = both(M_TableContext.Provider, TableContext.Provider);

  const M_TableQueryContext = React.createContext<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>
  >(
    undefined as unknown as UseQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey
    >,
  );
  const TableQueryProvider = both(
    M_TableQueryContext.Provider,
    TableQueryContext.Provider,
  );

  const M_TableQueryResultContext = React.createContext<
    UseQueryResult<TData, TError>
  >(undefined as unknown as UseQueryResult<TData, TError>);
  const TableQueryResultProvider = both(
    M_TableQueryResultContext.Provider,
    TableQueryResultContext.Provider,
  );

  const M_TableTotalQueryContext = React.createContext<
    UseQueryOptions<number, TError>
  >(undefined as unknown as UseQueryOptions<number, TError>);
  const TableTotalQueryProvider = both(
    M_TableTotalQueryContext.Provider,
    TableTotalQueryContext.Provider,
  );

  const M_TableTotalQueryResultContext = React.createContext<
    UseQueryResult<number, TError>
  >(undefined as unknown as UseQueryResult<number, TError>);
  const TableTotalQueryResultProvider = both(
    M_TableTotalQueryResultContext.Provider,
    TableTotalQueryResultContext.Provider,
  );

  const M_TableTotalContext = React.createContext<TableTotal>(
    undefined as unknown as TableTotal,
  );
  const TableTotalProvider = both(
    M_TableTotalContext.Provider,
    TableTotalContext.Provider,
  );

  const M_TableCountQueryContext = React.createContext<
    UseQueryOptions<number, TError>
  >(undefined as unknown as UseQueryOptions<number, TError>);
  const TableCountQueryProvider = both(
    M_TableCountQueryContext.Provider,
    TableCountQueryContext.Provider,
  );

  const M_TableCountQueryResultContext = React.createContext<
    UseQueryResult<number, TError>
  >(undefined as unknown as UseQueryResult<number, TError>);
  const TableCountQueryResultProvider = both(
    M_TableCountQueryResultContext.Provider,
    TableCountQueryResultContext.Provider,
  );

  const M_TableCountContext = React.createContext<TableCount>(
    undefined as unknown as TableCount,
  );
  const TableCountProvider = both(
    M_TableCountContext.Provider,
    TableCountContext.Provider,
  );

  const M_TableColumnsContext = React.createContext<ColumnDef<TRow>[]>(
    undefined as unknown as ColumnDef<TRow>[],
  );
  const TableColumnsProvider = both(
    M_TableColumnsContext.Provider,
    TableColumnsContext.Provider,
  );
  const M_TableBoxContext = React.createContext<RowDef<TRow>>(
    undefined as unknown as RowDef<TRow>,
  );
  const TableBoxProvider = both(
    M_TableBoxContext.Provider,
    TableBoxContext.Provider,
  );

  const M_TableQueryArgumentContext = React.createContext<
    WithSetter<TArgument>
  >(undefined as unknown as WithSetter<TArgument>);
  const TableQueryArgumentProvider = both(
    M_TableQueryArgumentContext.Provider,
    TableQueryArgumentContext.Provider,
  );

  const M_TableQueryOptionsContext = React.createContext<
    WithSetter<Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>>
  >(
    undefined as unknown as WithSetter<
      Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>
    >,
  );
  const TableQueryOptionsProvider = both(
    M_TableQueryOptionsContext.Provider,
    TableQueryOptionsContext.Provider,
  );

  const M_TableRowSelectionContext = React.createContext<
    WithSetter<RowSelectionState>
  >(undefined as unknown as WithSetter<RowSelectionState>);
  const TableRowSelectionProvider = both(
    M_TableRowSelectionContext.Provider,
    TableRowSelectionContext.Provider,
  );

  const M_TableColumnVisibilityContext = React.createContext<
    WithSetter<VisibilityState>
  >(undefined as unknown as WithSetter<VisibilityState>);
  const TableColumnVisibilityProvider = both(
    M_TableColumnVisibilityContext.Provider,
    TableColumnVisibilityContext.Provider,
  );

  const M_TableSettingsContext = React.createContext<TableSettingsContextValue>(
    undefined as unknown as TableSettingsContextValue,
  );
  const TableSettingsProvider = both(
    M_TableSettingsContext.Provider,
    TableSettingsContext.Provider,
  );

  const M_TableColumnFilterContext = React.createContext<
    WithSetter<ColumnFiltersState>
  >(undefined as unknown as WithSetter<ColumnFiltersState>);
  const TableColumnFilterProvider = both(
    M_TableColumnFilterContext.Provider,
    TableColumnFilterContext.Provider,
  );

  const M_TableSortingContext = React.createContext<WithSetter<SortingState>>(
    undefined as unknown as WithSetter<SortingState>,
  );
  const TableSortingProvider = both(
    M_TableSortingContext.Provider,
    TableSortingContext.Provider,
  );
  const M_TableColumnSizingContext = React.createContext<
    WithSetter<ColumnSizingState>
  >(undefined as unknown as WithSetter<ColumnSizingState>);
  const TableColumnSizingProvider = both(
    M_TableColumnSizingContext.Provider,
    TableColumnSizingContext.Provider,
  );

  const M_TablePositionContext = React.createContext<WithSetter<PositionState>>(
    undefined as unknown as WithSetter<PositionState>,
  );
  const TablePositionProvider = both(
    M_TablePositionContext.Provider,
    TablePositionContext.Provider,
  );

  const M_TablePreviewContext = React.createContext<WithSetter<PreviewState>>(
    undefined as unknown as WithSetter<PreviewState>,
  );
  const TablePreviewProvider = both(
    M_TablePreviewContext.Provider,
    TablePreviewContext.Provider,
  );

  const M_TableGalleryContext = React.createContext<WithSetter<GalleryState>>(
    undefined as unknown as WithSetter<GalleryState>,
  );
  const TableGalleryProvider = both(
    M_TableGalleryContext.Provider,
    TableGalleryContext.Provider,
  );

  const M_TableAllSelectedContext = React.createContext<
    WithSetter<AllSelectedState>
  >(undefined as unknown as WithSetter<AllSelectedState>);
  const TableAllSelectedProvider = both(
    M_TableAllSelectedContext.Provider,
    TableAllSelectedContext.Provider,
  );

  const M_TableAccordionGroupingContext = React.createContext<{
    state: AccordionGroupingState;
    actions: AccordionGroupingActions;
  }>(
    undefined as unknown as {
      state: AccordionGroupingState;
      actions: AccordionGroupingActions;
    },
  );
  const TableAccordionGroupingProvider = both(
    M_TableAccordionGroupingContext.Provider,
    TableAccordionGroupingContext.Provider,
  );

  const M_TableRowClassNameOptionsContext = React.createContext<
    RowClassNameOptions<TRow>
  >(undefined as unknown as RowClassNameOptions<TRow>);
  const TableRowClassNameOptionsProvider = both(
    M_TableRowClassNameOptionsContext.Provider,
    TableRowClassNameOptionsContext.Provider,
  );

  const M_TablePaginationContext = React.createContext<
    WithSetter<PaginationState>
  >(undefined as unknown as WithSetter<PaginationState>);
  const TablePaginationProvider = both(
    M_TablePaginationContext.Provider,
    TablePaginationContext.Provider,
  );

  const M_TableSearchContext = React.createContext<
    TableSearchContextValue<TSearchField, PathValue<TArgument, TSearchField>>
  >(undefined as unknown as TableSearchContextValue<TSearchField>);
  const TableSearchProvider = both(
    M_TableSearchContext.Provider,
    TableSearchContext.Provider,
  );

  const M_TableFiltersContext = React.createContext<
    TableFiltersContextValue<TArgument>
  >(undefined as unknown as TableFiltersContextValue<TArgument>);
  const TableFiltersProvider = both(
    M_TableFiltersContext.Provider,
    TableFiltersContext.Provider,
  );

  const M_TableSortOptionContext = React.createContext<
    TableSortOptionContextValue<PathValue<TArgument, TSortField>>
  >(
    undefined as unknown as TableSortOptionContextValue<
      PathValue<TArgument, TSortField>
    >,
  );
  const TableSortOptionProvider = both(
    M_TableSortOptionContext.Provider,
    TableSortOptionContext.Provider,
  );

  const M_TableItemTypeContext = React.createContext<string>(
    undefined as unknown as string,
  );
  const TableItemTypeProvider = both(
    M_TableItemTypeContext.Provider,
    TableItemTypeContext.Provider,
  );

  const M_TableCurrentItemRefContext = React.createContext<
    React.MutableRefObject<HTMLDivElement | null>
  >(undefined as unknown as React.MutableRefObject<HTMLDivElement | null>);
  const TableCurrentItemRefProvider = both(
    M_TableCurrentItemRefContext.Provider,
    TableCurrentItemRefContext.Provider,
  );

  const M_TableEventsContext = React.createContext<Emitter>(
    undefined as unknown as Emitter,
  );
  const TableEventsProvider = both(
    M_TableEventsContext.Provider,
    TableEventsContext.Provider,
  );

  const M_TablePinnedRowContext = React.createContext<
    TablePinnedRowContextValue<TPinnedRow<TRow>>
  >(undefined as unknown as TablePinnedRowContextValue<TPinnedRow<TRow>>);
  const TablePinnedRowProvider = both(
    M_TablePinnedRowContext.Provider,
    TablePinnedRowContext.Provider,
  );

  // const M_TableAutoRefreshContext = React.createContext<AutoRefreshConfig[]>(
  //   undefined as unknown as AutoRefreshConfig[],
  // );
  // const TableAutoRefreshProvider = both(
  //   M_TableAutoRefreshContext.Provider,
  //   TableAutoRefreshContext.Provider,
  // );

  const Provider = ({
    children,
    ...props
  }: Partial<
    TableContextProps<TRow, TArgument, TQueryFnData, TError, TData, TQueryKey>
  > & {
    children?: React.ReactNode | undefined;
  }) => {
    const queryOptionsState =
      use((props.queryOptionsState ?? _default?.queryOptionsState)!) ??
      React.useState<
        Partial<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>
      >(use((props.queryOptions ?? _default?.queryOptions)!));
    const [queryOptions, _setQueryOptions] = queryOptionsState;

    const _queryArgumentState =
      use((props.queryArgumentState ?? _default?.queryArgumentState)!) ??
      React.useState<TArgument>(
        use((props.queryArgument ?? _default?.queryArgument)!),
      );
    const [rawQueryArgument, _setQueryArgument] = _queryArgumentState;
    // Every consumer of the table argument reads it from here -- the list query, the chips, and
    // the bodies of downloads, bulk actions and deploys. The filter actions put the router's clear
    // flag into it; a URL-backed table hands that on to the router, but a table holding its own
    // state (the Deploy dialog's device picker, say) just stores it. Either way it is not table
    // state, so it stops here (ID-7067).
    const queryArgument = React.useMemo(
      () => withoutClearFlag(rawQueryArgument),
      [rawQueryArgument],
    );
    const onQueryArgumentChangeRef = useCurrentRef(
      props?.onQueryArgumentChange ?? _default?.onQueryArgumentChange,
    );
    const setQueryArgument = React.useCallback<typeof _setQueryArgument>(
      (a) => {
        return _setQueryArgument((s) => {
          const next = functionalUpdate(a, s);
          if (onQueryArgumentChangeRef.current) {
            onQueryArgumentChangeRef.current(next);
          }
          return next;
        });
      },
      [_setQueryArgument],
    );
    const queryArgumentState = [queryArgument, setQueryArgument] as [
      typeof queryArgument,
      typeof setQueryArgument,
    ];
    const sortField = use(
      props?.sortField ?? _default?.sortField,
    ) as TSortField;
    const defaultSort = use(props?.defaultSort ?? _default?.defaultSort);
    const hasSortingInteractionRef = React.useRef(false);

    const effectiveQueryArgument = React.useMemo(() => {
      if (!isDefined(sortField)) {
        return queryArgument;
      }

      const currentSort = normalizeSortValues(
        (queryArgument as any)?.[sortField],
      );
      if (currentSort.length > 0 || hasSortingInteractionRef.current) {
        return queryArgument;
      }

      const normalizedDefaultSort = normalizeSortValues(defaultSort);
      if (normalizedDefaultSort.length === 0) {
        return queryArgument;
      }

      return {
        ...queryArgument,
        [sortField]: normalizedDefaultSort,
      } as TArgument;
    }, [queryArgument, sortField, defaultSort]);

    const enableCountOptimization =
      props?.enableCountOptimization ??
      _default?.enableCountOptimization ??
      false;
    const hasOptimizedRef = useRef<boolean>(false);
    const [optimizedLimit, setOptimizedLimit] = React.useState<
      number | undefined
    >(undefined);

    // Reset optimization when query argument changes (filter/pagination/clear)
    useEffect(() => {
      if (!hasOptimizedRef.current) return;
      hasOptimizedRef.current = false;
      setOptimizedLimit(undefined);
    }, [queryArgument]);

    // Original items query — always runs
    const tableQueryOriginal = {
      ...(props.queryCreator ?? _default?.queryCreator)!(
        effectiveQueryArgument,
      ),
      ...queryOptions,
    };
    const tableQueryResultOriginal = useQuery(tableQueryOriginal);

    // Optimized items query — runs in parallel when count < limit
    const effectiveQueryArgumentOptimized = React.useMemo(
      () =>
        optimizedLimit !== undefined
          ? { ...effectiveQueryArgument, limit: optimizedLimit }
          : effectiveQueryArgument,
      [effectiveQueryArgument, optimizedLimit],
    );
    const tableQueryOptimized = {
      ...(props.queryCreator ?? _default?.queryCreator)!(
        effectiveQueryArgumentOptimized,
      ),
      ...queryOptions,
      enabled:
        optimizedLimit !== undefined && (tableQueryOriginal.enabled ?? true),
    };
    const tableQueryResultOptimized = useQuery(tableQueryOptimized);

    // Effective result: use optimized if it won, otherwise original
    const tableQueryResult =
      optimizedLimit !== undefined && tableQueryResultOptimized.isSuccess
        ? tableQueryResultOptimized
        : tableQueryResultOriginal;
    const tableQuery =
      optimizedLimit !== undefined && tableQueryResultOptimized.isSuccess
        ? tableQueryOptimized
        : tableQueryOriginal;

    const getRowId_ = props.getRowId ?? _default?.getRowId;
    const getRowId = React.useMemo(() => {
      if (!getRowId_) return getRowId_;
      return (
        originalRow: TRow,
        index: number,
        parent?: Row<TRow> | undefined,
      ) => {
        return getRowId_(
          originalRow,
          index +
            ((tableQueryResult?.data?.page ?? queryArgument?.page ?? 1) - 1) *
              (tableQueryResult?.data?.limit ?? queryArgument?.limit ?? 0),
          parent,
        );
      };
    }, [
      getRowId_,
      tableQueryResult?.data?.page,
      tableQueryResult?.data?.limit,
    ]);

    const pinnedRowState =
      use((props.pinnedRowState ?? _default?.pinnedRowState)!) ??
      React.useState<TRow>(use((props.pinnedRow ?? _default?.pinnedRow)!));
    const [pinnedRow_, _setPinnedRow] = pinnedRowState;
    const pinnedRow = React.useMemo(
      () =>
        pinnedRow_
          ? { ...pinnedRow_, __meta: { ...pinnedRow_?.__meta, pinned: true } }
          : undefined,
      [pinnedRow_],
    );
    const rowPinning = React.useMemo<RowPinningState>(
      () => ({
        top: pinnedRow_ && getRowId ? [getRowId(pinnedRow_, 0)] : undefined,
        bottom: undefined,
      }),
      [pinnedRow_, getRowId],
    );

    const data_ = tableQueryResult?.data?.results;
    const data = React.useMemo(() => {
      if (!pinnedRow) {
        return data_ ?? [];
      }
      if (!getRowId) {
        return [pinnedRow, ...(data_ ?? [])];
      }
      const pinnedRowId = getRowId(pinnedRow, 0);
      return [
        pinnedRow,
        ...(data_ ?? []).filter((row, i) => getRowId(row, i) !== pinnedRowId),
      ];
    }, [pinnedRow, data_, getRowId_]);
    const columns = use((props.columns ?? _default?.columns)!) ?? [];
    const box = (props.box ?? _default?.box)! ?? undefined;

    const manualPagination =
      props.manualPagination ?? _default?.manualPagination ?? true;

    const rowSelectionState = React.useState<RowSelectionState>({});
    const [rowSelection, setRowSelection] = rowSelectionState;

    // Reset to default also clears the table's sort: the stored sort is gone, so the URL must not
    // keep it, and the declared `defaultSort` applies again.
    const onResetSort = React.useCallback(() => {
      if (!isDefined(sortField)) {
        return;
      }
      hasSortingInteractionRef.current = false;
      // Nothing to clear: no navigation, and the user keeps the page they are on.
      if (
        normalizeSortValues((queryArgument as any)?.[sortField]).length === 0
      ) {
        return;
      }
      setQueryArgument(
        (prev) =>
          ({ ...prev, [sortField as any]: undefined, page: 1 }) as TArgument,
      );
    }, [queryArgument, setQueryArgument, sortField]);
    const tableSettings = useTableSettingsState({
      tableKey: props.tableKey ?? _default?.tableKey,
      columns,
      // The URL's own sort, not `effectiveQueryArgument`: an injected `defaultSort` is a default,
      // not a preference, and must never be written to the profile.
      sort: parseSortValues(
        isDefined(sortField) ? (queryArgument as any)?.[sortField] : undefined,
      ),
      pageSize: queryArgument?.limit,
      onResetSort,
    });
    const markInteractionRef = useCurrentRef(tableSettings.markInteraction);
    const { columnVisibility, onColumnVisibilityChange: setColumnVisibility } =
      tableSettings;
    const columnVisibilityState = React.useMemo<WithSetter<VisibilityState>>(
      () => [columnVisibility, setColumnVisibility],
      [columnVisibility, setColumnVisibility],
    );

    const columnFiltersState = React.useState<ColumnFiltersState>([]);
    const [columnFilters, setColumnFilters] = columnFiltersState;

    const sortingValue = React.useMemo<SortingState>(() => {
      const values = normalizeSortValues(
        (effectiveQueryArgument as any)?.[sortField],
      );
      return values.map((v) => {
        try {
          const parsed = JSON.parse(v);
          return { id: parsed.field, desc: parsed.dir === "desc" };
        } catch {
          return { id: v, desc: false };
        }
      });
    }, [effectiveQueryArgument, sortField]);

    const setSortingValue = React.useCallback<OnChangeFn<SortingState>>(
      (updater) => {
        if (isDefined(sortField)) {
          markInteractionRef.current();
        }
        setQueryArgument((prev) => {
          if (!isDefined(sortField)) {
            return prev;
          }

          const previousSortValues = normalizeSortValues(
            (prev as any)?.[sortField],
          );
          const prevSortingSource = previousSortValues;

          hasSortingInteractionRef.current = true;

          const prevSorting: SortingState = prevSortingSource.map((v) => {
            try {
              const parsed = JSON.parse(v);
              return { id: parsed.field, desc: parsed.dir === "desc" };
            } catch {
              return { id: v, desc: false };
            }
          });
          const nextSorting = functionalUpdate(updater, prevSorting);
          const nextSortBy = nextSorting.map((s) =>
            JSON.stringify({ field: s.id, dir: s.desc ? "desc" : "asc" }),
          );
          return {
            ...prev,
            [sortField as any]: nextSortBy.length > 0 ? nextSortBy : undefined,
            page: 1,
          } as TArgument;
        });
      },
      [setQueryArgument, sortField],
    );

    const sortingState = React.useMemo<WithSetter<SortingState>>(
      () => [sortingValue, setSortingValue],
      [sortingValue, setSortingValue],
    );
    const [sorting, setSorting] = sortingState;

    const columnSizingState = React.useState<ColumnSizingState>({});
    const [columnSizing, setColumnSizing] = columnSizingState;

    const page = tableQueryResult.data?.page ?? queryArgument?.page;
    const limit = tableQueryResult.data?.limit ?? queryArgument?.limit;

    const totalQueryCreator =
      props?.totalQueryCreator ?? _default?.totalQueryCreator;
    const tableTotalQuery = totalQueryCreator
      ? totalQueryCreator(queryArgument)
      : ({ enabled: false } as UseQueryOptions<number, TError>);

    const tableTotalQueryResult = useQuery(tableTotalQuery);

    const tableTotal = React.useMemo<TableTotal>(() => {
      const value = tableTotalQueryResult.data ?? tableQueryResult.data?.total;
      const isEstimate = isDefined(tableTotalQueryResult.data)
        ? false
        : tableQueryResult.data?.totalIsEstimate;
      const temporaryEstimate =
        isEstimate && tableTotalQuery?.enabled
          ? undefined
          : tableQueryResult.data?.total;
      const isLoading = tableTotalQuery?.enabled
        ? tableTotalQueryResult?.isLoading
        : tableQueryResult.isLoading;
      return { value, isEstimate, temporaryEstimate, isLoading };
    }, [tableQueryResult, tableTotalQueryResult, tableTotalQuery]);

    const countQueryCreator =
      props?.countQueryCreator ?? _default?.countQueryCreator;
    const tableCountQuery = countQueryCreator
      ? countQueryCreator(queryArgument)
      : ({ enabled: false } as UseQueryOptions<number, TError>);

    const tableCountQueryResult = useQuery(tableCountQuery);

    const ignoreQueryCount =
      props?.ignoreQueryCount ?? _default?.ignoreQueryCount;

    const tableCount = React.useMemo<TableCount>(() => {
      const value =
        tableCountQueryResult.data ??
        (ignoreQueryCount ? undefined : tableQueryResult.data?.count);
      const isEstimate = isDefined(tableCountQueryResult.data)
        ? false
        : ignoreQueryCount
          ? undefined
          : tableQueryResult.data?.countIsEstimate;
      const temporaryEstimate =
        isEstimate && tableCountQuery?.enabled
          ? undefined
          : ignoreQueryCount
            ? undefined
            : tableQueryResult.data?.count;
      const isLoading = tableCountQuery?.enabled
        ? tableCountQueryResult?.isLoading
        : ignoreQueryCount
          ? undefined
          : tableQueryResult.isLoading;
      return {
        value,
        isEstimate,
        temporaryEstimate,
        isLoading,
        ignoreQueryCount,
      };
    }, [
      tableQueryResult,
      tableCountQueryResult,
      tableCountQuery,
      ignoreQueryCount,
    ]);

    const rowCount = tableTotal?.isEstimate ? undefined : tableTotal?.value;

    const pagination = React.useMemo<PaginationState>(
      () => ({ pageIndex: page - 1, pageSize: limit }),
      [page, limit],
    );

    const setPagination: OnChangeFn<PaginationState> = React.useCallback(
      (a) =>
        setQueryArgument((q) =>
          functionalUpdateMap(
            a,
            q,
            (t) => ({
              pageIndex: t.page - 1,
              pageSize: t.limit,
            }),
            (t) => {
              if (t.pageSize !== q.limit) {
                // A page-size pick is a preference worth keeping; moving between pages is not.
                markInteractionRef.current();
              }
              return {
                page: t.pageIndex + 1,
                limit: t.pageSize,
              } as Partial<TArgument>;
            },
          ),
        ),
      [setQueryArgument],
    );

    const paginationState: WithSetter<PaginationState> = React.useMemo(
      () => [pagination, setPagination],
      [pagination, setPagination],
    );
    const positionState =
      use((props.positionState ?? _default?.positionState)!) ??
      React.useState<PositionState>(
        use((props.position ?? _default?.position)!) ?? {},
      );
    const [position, setPosition] = positionState;
    const skipPreview = use(props.skipPreview ?? _default?.skipPreview);
    const onPreviewClose = props.onPreviewClose ?? _default?.onPreviewClose;
    const previewState =
      use((props.previewState ?? _default?.previewState)!) ??
      React.useState<PreviewState>(use((props.preview ?? _default?.preview)!));
    const [preview, setPreview] = previewState;
    const galleryState =
      use((props.galleryState ?? _default?.galleryState)!) ??
      React.useState<GalleryState>(
        use((props.gallery ?? _default?.gallery)!) ?? false,
      );
    const [gallery, setGallery] = galleryState;

    const enableGallery = use(
      (props.enableGallery ?? _default?.enableGallery)!,
    );
    const allSelectedState =
      use((props.allSelectedState ?? _default?.allSelectedState)!) ??
      React.useState<GalleryState>(
        use((props.allSelected ?? _default?.allSelected)!) ?? false,
      );
    const [allSelected, setAllSelected] = allSelectedState;

    const initialGroupByColumn = use(
      props?.groupByColumn ?? _default?.groupByColumn,
    );
    const accordionGrouping = useAccordionGrouping(initialGroupByColumn);
    const getRowClassName = props?.getRowClassName ?? _default?.getRowClassName;

    const table = useReactTable<TRow>({
      _features: [
        PositionFeature,
        PreviewFeature,
        GalleryFeature,
        AllSelectedFeature,
        RowClassNameFeature,
      ],
      data,
      columns,
      state: {
        sorting,
        columnVisibility,
        rowSelection,
        columnFilters,
        columnSizing,
        pagination,
        position,
        preview,
        gallery,
        allSelected,
        rowPinning,
      },
      getRowId: getRowId,
      enableRowSelection: true,
      enableMultiRowSelection:
        props.enableMultiRowSelection ??
        _default?.enableMultiRowSelection ??
        true,
      isMultiSortEvent: () => true,
      enableSortingRemoval: true,
      enableMultiRemove: true,
      onRowSelectionChange: setRowSelection,
      onSortingChange: setSorting,
      onColumnFiltersChange: setColumnFilters,
      onColumnVisibilityChange: setColumnVisibility,
      onPaginationChange: setPagination,
      onPositionChange: setPosition,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFacetedRowModel: getFacetedRowModel(),
      getFacetedUniqueValues: getFacetedUniqueValues(),
      onPreviewChange: setPreview,
      onPreviewClose: onPreviewClose,
      onGalleryChange: setGallery,
      onAllSelectedChange: setAllSelected,
      getRowClassName: getRowClassName,

      enableColumnResizing: true,
      columnResizeMode: "onChange",
      onColumnSizingChange: setColumnSizing,

      manualPagination: manualPagination,
      rowCount: rowCount,

      defaultColumn: {
        minSize: 1,
        maxSize: Number.MAX_SAFE_INTEGER,
        sortDescFirst: false,
      },
      // autoResetPageIndex: false,
      enableGallery: enableGallery ?? gallery,
      skipPreview,
      enableRowPinning: true,
    });

    const searchLabel = use(props?.searchLabel ?? _default?.searchLabel);
    const searchField = use(
      props?.searchField ?? _default?.searchField,
    ) as TSearchField;
    const searchValidation =
      props.searchValidation ?? _default?.searchValidation;
    const searchState = asStateProperty(queryArgumentState, searchField, {
      page: 1,
    } as Partial<TArgument>);

    const defaultFilters = use(
      props?.defaultFilters ?? _default?.defaultFilters,
    );
    const filterBreadcrumbs = use(
      props?.filterBreadcrumbs ?? _default?.filterBreadcrumbs,
    );

    const defaultFiltersApply = React.useMemo(() => {
      if (!defaultFilters) return undefined;
      return Object.fromEntries(
        Object.entries(defaultFilters).map(([key, value]) => [
          key,
          Array.isArray(value) ? value?.[0] : value,
        ]),
      );
    }, [defaultFilters]) as {
      [k in keyof TArgument]: TArgument[k];
    };

    const isFiltersActive = React.useMemo(() => {
      const _defaultFilters = defaultFilters ?? {};
      return (
        Object.entries(queryArgument).filter(
          ([key, value]) =>
            key in _defaultFilters &&
            !isDefault(value, _defaultFilters?.[key] ?? undefined),
        ).length > 0
      );
    }, [queryArgument, setQueryArgument, defaultFilters]);
    const clearFilters = React.useCallback(() => {
      setQueryArgument((a) => ({
        ...a,
        ...defaultFiltersApply,
        [CLEAR_FLAG]: true,
      }));
    }, [setQueryArgument, defaultFiltersApply]);

    const { t } = useTranslation();

    const sortState = asStateProperty(queryArgumentState, sortField, {
      page: 1,
    } as Partial<TArgument>);
    const [sort, setSort] = sortState;

    const getSortOption =
      props?.getSortOption ??
      _default?.getSortOption ??
      ((value: any) => (!value ? undefined : { value, label: t(value) }));

    const sortEnum = props?.sortEnum ?? _default?.sortEnum;
    const sortOption = React.useMemo(
      () => getSortOption(sort as any),
      [sort, getSortOption, t],
    );
    const sortOptions = React.useMemo(
      () => (sortEnum ?? []).map(getSortOption).filter(Boolean),
      [getSortOption, sortEnum],
    );
    const setSortOption = React.useCallback(
      (a: React.SetStateAction<OptionType<any> | undefined>) => {
        setSort((s) => {
          const before = getSortOption(s as any);
          const after = functionalUpdate(a, before);
          return after?.value;
        });
      },
      [getSortOption, setSort],
    );
    const sortOptionState = [sortOption, setSortOption] as WithSetter<
      OptionType<PathValue<TArgument, TSortField>>
    >;

    const itemType = (props.itemType ?? _default?.itemType)!;
    const currentRef = use(
      props?.currentRef ??
        _default?.currentRef ??
        React.useRef<HTMLDivElement | null>,
    )! as React.MutableRefObject<HTMLDivElement | null>;
    const eventEmitter = React.useMemo(() => createNanoEvents(), []);

    const queryClient = useQueryClient();
    const autoRefresh = props?.autoRefresh ?? _default?.autoRefresh ?? [];
    const refreshCounter = React.useRef(0);

    // Reset counter only on mount (route changes), not on every effect run
    React.useEffect(() => {
      refreshCounter.current = 0;
    }, []);

    React.useEffect(() => {
      if (!autoRefresh || autoRefresh.length === 0) return;

      const latestRows = table
        .getRowModel()
        .rows.map((row: any) => row.original);
      const activeConfigs = autoRefresh.filter((cfg) =>
        cfg.lookFor(latestRows),
      );

      if (activeConfigs.length === 0) {
        refreshCounter.current = 0;
        return;
      }

      const interval = Math.min(
        ...activeConfigs.map((cfg) => cfg.interval ?? 5000),
      );
      const maxRefreshes = Math.max(
        ...activeConfigs.map((cfg) => cfg.maxRefreshes ?? Infinity),
      );
      if (refreshCounter.current >= maxRefreshes) {
        return;
      }

      const refresh = () => {
        queryClient.invalidateQueries({ queryKey: tableQuery.queryKey });
        if (tableTotalQuery.queryKey) {
          queryClient.invalidateQueries({ queryKey: tableTotalQuery.queryKey });
        }
        eventEmitter.emit("refresh");
        refreshCounter.current += 1;
      };

      const timeoutId = setTimeout(refresh, interval);
      return () => {
        clearTimeout(timeoutId);
      };
    }, [
      autoRefresh,
      queryClient,
      tableQuery.queryKey,
      tableTotalQuery.queryKey,
      eventEmitter,
      data,
    ]);

    // Phase 1: race between items (original) and count.
    // - Items finish first: cancel count, no optimization needed.
    // - Count finishes first and count === 0: cancel original items, show "no results".
    // - Count finishes first and count >= limit: cancel count, items are fine as-is.
    // - Count finishes first and count < limit: start parallel optimized items request.
    useEffect(() => {
      if (!enableCountOptimization) return;
      if (hasOptimizedRef.current) return;
      if (!tableCountQuery.enabled) return;

      const countValue = tableCountQueryResult.data;
      const countLoading = tableCountQueryResult.isLoading;
      const itemsLoading = tableQueryResultOriginal.isLoading;
      const currentLimit = queryArgument?.limit ?? 200;

      // Items finished first — cancel count, optimization no longer needed
      if (!itemsLoading && countLoading) {
        queryClient.cancelQueries({ queryKey: tableCountQuery.queryKey });
        hasOptimizedRef.current = true;
        return;
      }

      // Count finished first
      if (countValue !== undefined && itemsLoading) {
        hasOptimizedRef.current = true;

        // Count === 0: cancel items, show "no results"
        if (countValue === 0) {
          queryClient.cancelQueries({ queryKey: tableQueryOriginal.queryKey });
          return;
        }

        // Count >= limit: items are already fetching correctly, cancel count
        if (countValue >= currentLimit) {
          queryClient.cancelQueries({ queryKey: tableCountQuery.queryKey });
          return;
        }

        // Count < limit: start parallel optimized items request (Phase 2 race begins)
        setOptimizedLimit(countValue);
      }
    }, [
      enableCountOptimization,
      tableCountQueryResult.data,
      tableCountQueryResult.isLoading,
      tableCountQuery.enabled,
      tableCountQuery.queryKey,
      tableQueryResultOriginal.isLoading,
      queryArgument?.limit,
      tableQueryOriginal.queryKey,
      queryClient,
      setOptimizedLimit,
    ]);

    // Phase 2: race between original and optimized items requests.
    // Whoever finishes first wins; the other gets cancelled.
    useEffect(() => {
      if (!enableCountOptimization || optimizedLimit === undefined) return;

      // Optimized finished first — cancel original
      if (
        tableQueryResultOptimized.isSuccess &&
        tableQueryResultOriginal.isLoading
      ) {
        queryClient.cancelQueries({ queryKey: tableQueryOriginal.queryKey });
      }

      // Original finished first — cancel optimized, clean up
      if (
        tableQueryResultOriginal.isSuccess &&
        tableQueryResultOptimized.isLoading
      ) {
        queryClient.cancelQueries({ queryKey: tableQueryOptimized.queryKey });
        setOptimizedLimit(undefined);
      }
    }, [
      enableCountOptimization,
      optimizedLimit,
      tableQueryResultOriginal.isSuccess,
      tableQueryResultOriginal.isLoading,
      tableQueryResultOptimized.isSuccess,
      tableQueryResultOptimized.isLoading,
      tableQueryOriginal.queryKey,
      tableQueryOptimized.queryKey,
      queryClient,
    ]);

    return (
      <TableColumnsProvider value={columns}>
        <TableBoxProvider value={box}>
          <TableQueryOptionsProvider value={queryOptionsState}>
            <TableQueryArgumentProvider value={queryArgumentState}>
              <TableQueryProvider value={tableQuery}>
                <TableQueryResultProvider value={tableQueryResult}>
                  <TableTotalQueryProvider value={tableTotalQuery}>
                    <TableTotalQueryResultProvider
                      value={tableTotalQueryResult}
                    >
                      <TableTotalProvider value={tableTotal}>
                        <TableCountQueryProvider value={tableCountQuery}>
                          <TableCountQueryResultProvider
                            value={tableCountQueryResult}
                          >
                            <TableCountProvider value={tableCount}>
                              <TableRowSelectionProvider
                                value={rowSelectionState}
                              >
                                <TableColumnVisibilityProvider
                                  value={columnVisibilityState}
                                >
                                  <TableSettingsProvider value={tableSettings}>
                                    <TableColumnFilterProvider
                                      value={columnFiltersState}
                                    >
                                      <TableSortingProvider
                                        value={sortingState}
                                      >
                                        <TableColumnSizingProvider
                                          value={columnSizingState}
                                        >
                                          <TablePositionProvider
                                            value={positionState}
                                          >
                                            <TablePaginationProvider
                                              value={paginationState}
                                            >
                                              <TablePreviewProvider
                                                value={previewState}
                                              >
                                                <TableGalleryProvider
                                                  value={galleryState}
                                                >
                                                  <TableAllSelectedProvider
                                                    value={allSelectedState}
                                                  >
                                                    <TableAccordionGroupingProvider
                                                      value={accordionGrouping}
                                                    >
                                                      <TableRowClassNameOptionsProvider
                                                        value={{
                                                          getRowClassName,
                                                        }}
                                                      >
                                                        <TablePinnedRowProvider
                                                          value={pinnedRowState}
                                                        >
                                                          <TableProvider
                                                            value={table}
                                                          >
                                                            <TableSearchProvider
                                                              value={{
                                                                searchState:
                                                                  searchState as any,
                                                                searchField,
                                                                searchLabel,
                                                                searchValidation,
                                                              }}
                                                            >
                                                              <TableFiltersProvider
                                                                value={{
                                                                  clearFilters,
                                                                  defaultFilters,
                                                                  defaultFiltersApply,
                                                                  filterBreadcrumbs,
                                                                  isFiltersActive,
                                                                }}
                                                              >
                                                                <TableSortOptionProvider
                                                                  value={{
                                                                    sortOptionState:
                                                                      sortOptionState as any,
                                                                    sortOptions:
                                                                      sortOptions as any,
                                                                    getSortOption,
                                                                    sortEnum:
                                                                      sortEnum as any,
                                                                  }}
                                                                >
                                                                  <TableItemTypeProvider
                                                                    value={
                                                                      itemType
                                                                    }
                                                                  >
                                                                    <TableCurrentItemRefProvider
                                                                      value={
                                                                        currentRef
                                                                      }
                                                                    >
                                                                      <TableEventsProvider
                                                                        value={
                                                                          eventEmitter
                                                                        }
                                                                      >
                                                                        {/* <TableAutoRefreshProvider
                                                                    value={
                                                                      autoRefresh
                                                                    }
                                                                  > */}
                                                                        {
                                                                          children
                                                                        }
                                                                        {/* </TableAutoRefreshProvider> */}
                                                                      </TableEventsProvider>
                                                                    </TableCurrentItemRefProvider>
                                                                  </TableItemTypeProvider>
                                                                </TableSortOptionProvider>
                                                              </TableFiltersProvider>
                                                            </TableSearchProvider>
                                                          </TableProvider>
                                                        </TablePinnedRowProvider>
                                                      </TableRowClassNameOptionsProvider>
                                                    </TableAccordionGroupingProvider>
                                                  </TableAllSelectedProvider>
                                                </TableGalleryProvider>
                                              </TablePreviewProvider>
                                            </TablePaginationProvider>
                                          </TablePositionProvider>
                                        </TableColumnSizingProvider>
                                      </TableSortingProvider>
                                    </TableColumnFilterProvider>
                                  </TableSettingsProvider>
                                </TableColumnVisibilityProvider>
                              </TableRowSelectionProvider>
                            </TableCountProvider>
                          </TableCountQueryResultProvider>
                        </TableCountQueryProvider>
                      </TableTotalProvider>
                    </TableTotalQueryResultProvider>
                  </TableTotalQueryProvider>
                </TableQueryResultProvider>
              </TableQueryProvider>
            </TableQueryArgumentProvider>
          </TableQueryOptionsProvider>
        </TableBoxProvider>
      </TableColumnsProvider>
    );
  };
  return {
    downloadCsv: _default?.downloadCsv,
    Provider,
    TableContext: M_TableContext,
    TableQueryContext: M_TableQueryContext,
    TableQueryOptionsContext: M_TableQueryOptionsContext,
    TableQueryArgumentContext: M_TableQueryArgumentContext,
    TableColumnsContext: M_TableColumnsContext,
    TableRowSelectionContext: M_TableRowSelectionContext,
    TableColumnVisibilityContext: M_TableColumnVisibilityContext,
    TableSettingsContext: M_TableSettingsContext,
    TableColumnFilterContext: M_TableColumnFilterContext,
    TableSortingContext: M_TableSortingContext,
    TableColumnSizingContext: M_TableColumnSizingContext,
    TablePositionContext: M_TablePositionContext,
    TablePaginationContext: M_TablePaginationContext,
    TablePreviewContext: M_TablePreviewContext,
    TableGalleryContext: M_TableGalleryContext,
    TableAllSelectedContext: M_TableAllSelectedContext,
    TableAccordionGroupingContext: M_TableAccordionGroupingContext,
    TableSearchContext: M_TableSearchContext,
    TableFiltersContext: M_TableFiltersContext,
    TableSortOptionContext: M_TableSortOptionContext,
    TableItemTypeContext: M_TableItemTypeContext,
    TableEventsContext: M_TableEventsContext,

    useTable: () => useContext(M_TableContext),
    useTableQuery: () => useContext(M_TableQueryContext),
    useTableQueryResult: () => useContext(M_TableQueryResultContext),
    useTableQueryOptions: () => useContext(M_TableQueryOptionsContext),
    useTableQueryArgument: () => useContext(M_TableQueryArgumentContext),
    useTableTotalQuery: () => useContext(M_TableTotalQueryContext),
    useTableTotalQueryResult: () => useContext(M_TableTotalQueryResultContext),
    useTableTotal: () => useContext(M_TableTotalContext),
    useTableCountQuery: () => useContext(M_TableCountQueryContext),
    useTableCountQueryResult: () => useContext(M_TableCountQueryResultContext),
    useTableCount: () => useContext(M_TableCountContext),
    useTableColumns: () => useContext(M_TableColumnsContext),
    useTableBox: () => useContext(M_TableBoxContext),
    useTableRowSelection: () => useContext(M_TableRowSelectionContext),
    useTableColumnVisibility: () => useContext(M_TableColumnVisibilityContext),
    useTableSettings: () => useContext(M_TableSettingsContext),
    useTableColumnFilter: () => useContext(M_TableColumnFilterContext),
    useTableSorting: () => useContext(M_TableSortingContext),
    useTableColumnSizing: () => useContext(M_TableColumnSizingContext),
    useTablePosition: () => useContext(M_TablePositionContext),
    useTablePagination: () => useContext(M_TablePaginationContext),
    useTablePreview: () => useContext(M_TablePreviewContext),
    useTableGallery: () => useContext(M_TableGalleryContext),
    useTableAllSelected: () => useContext(M_TableAllSelectedContext),
    useTableAccordionGrouping: () =>
      useContext(M_TableAccordionGroupingContext),
    useTableSearch: () => useContext(M_TableSearchContext),
    useTableFilters: () => useContext(M_TableFiltersContext),
    useTableSortOption: () => useContext(M_TableSortOptionContext),
    useTableItemType: () => useContext(M_TableItemTypeContext),
    useTableRefresh: () => {
      const queryClient = useQueryClient();
      const tableQuery = useContext(M_TableQueryContext);
      const tableTotalQuery = useContext(M_TableTotalQueryContext);
      // const tableCountQuery = useContext(M_TableCountQueryContext);
      const eventsEmitter = useContext(M_TableEventsContext);
      return () => {
        queryClient.invalidateQueries({ queryKey: tableQuery.queryKey });
        if (tableTotalQuery.queryKey) {
          queryClient.invalidateQueries({ queryKey: tableTotalQuery.queryKey });
        }
        // if (tableCountQuery.queryKey) {
        //   queryClient.invalidateQueries({ queryKey: tableCountQuery.queryKey });
        // }
        eventsEmitter.emit("refresh");
      };
    },
    useTableDeleteRefresh: () => {
      const queryClient = useQueryClient();
      const tableQuery = useContext(M_TableQueryContext);
      const tableTotalQuery = useContext(M_TableTotalQueryContext);
      const eventsEmitter = useContext(M_TableEventsContext);
      const table = useContext(M_TableContext);
      // `positionalId`: pass true for tables whose id is the row's array
      // index (e.g. queue pending/failures, deleted by position) rather
      // than a stable entity id. Deleting any row at or before the
      // previewed index shifts a different row into that index, so the
      // previewed index no longer refers to the same record even though
      // "something" is still there — close whenever the deleted index is
      // <= the previewed one, not just on exact match.
      return async (id: string | number, positionalId = false) => {
        await queryClient.invalidateQueries({ queryKey: tableQuery.queryKey });
        if (tableTotalQuery.queryKey) {
          await queryClient.invalidateQueries({
            queryKey: tableTotalQuery.queryKey,
          });
        }
        eventsEmitter.emit("refresh");
        if (!table) return;
        const position = table.getState().position;
        if (position?.id === undefined) return;
        if (positionalId) {
          const deletedIndex = Number(id);
          const previewedIndex = Number(position.id);
          if (
            !Number.isNaN(deletedIndex) &&
            !Number.isNaN(previewedIndex) &&
            deletedIndex <= previewedIndex
          ) {
            table.closePreview();
          }
          return;
        }
        if (String(position.id) !== String(id)) {
          return;
        }
        // `table` is a stale closure captured at the last render before this
        // click, so its row model still reflects pre-delete data even after
        // the invalidateQueries above resolves. Check the freshly refetched
        // cache data directly instead of table.getRow, which would never
        // throw here.
        const getRowId = table?.options?.getRowId;
        if (!getRowId) return;
        const freshData = queryClient.getQueryData<
          PaginatedResponseWithCountDto<TRow>
        >(tableQuery.queryKey);
        const stillPresent = getRowId
          ? freshData?.results.some(
              (row, index) => getRowId(row, index, undefined) === String(id),
            )
          : false;
        if (!stillPresent) {
          table.closePreview();
        }
      };
    },
    useTableCurrentItemRef: () => useContext(M_TableCurrentItemRefContext),
    useTableEvents: () => useContext(M_TableEventsContext),
    // useTableAutoRefresh: () => useContext(M_TableAutoRefreshContext),
    useTablePinnedRow: () => useContext(M_TablePinnedRowContext),
  };
};

export type DataTableContextData<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
> = Omit<
  ReturnType<
    typeof createTableContext<
      TRow,
      TArgument,
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TSearchField,
      TSortField
    >
  >,
  "Provider"
>;

export type DataTableContext<
  TRow = unknown,
  TArgument extends PaginationRequest = PaginationRequest,
  TQueryFnData extends
    PaginatedResponseWithCountDto<TRow> = PaginatedResponseWithCountDto<TRow>,
  TError = ApiError,
  TData extends TQueryFnData = TQueryFnData,
  TQueryKey extends QueryKey = any,
  TSearchField extends FieldPath<TArgument> = FieldPath<TArgument>,
  TSortField extends FieldPath<TArgument> = FieldPath<TArgument>,
> = ReturnType<
  typeof createTableContext<
    TRow,
    TArgument,
    TQueryFnData,
    TError,
    TData,
    TQueryKey,
    TSearchField,
    TSortField
  >
>;

export function useFilterIsActive(
  key: string | string[] | Record<string, any>,
) {
  const [args] = useTableQueryArgument();
  const { defaultFilters } = useTableFilters();
  if (typeof key === "string")
    return !isDefault(args?.[key], defaultFilters?.[key] ?? undefined);
  return (
    Array.isArray(key)
      ? key.map((k) => [k, undefined] as const)
      : Object.entries(key)
  ).reduce(
    (prev, [k, v]) =>
      prev || !isDefault(args?.[k], v ?? defaultFilters?.[k] ?? undefined),
    false,
  );
}
